import * as React from 'react';

import type {
    SalesDocumentUpdateInput,
    GetUserNames_users as UsersConnection,
} from 'nutshell-graphql-types';

import type {SalesDocumentType} from '../../lead-sidebar/sales-documents/types';
import {SaveableWrapper} from '../../saveable-wrapper/saveable-wrapper';

import {QuotePreparedBy} from '../editable/quote-prepared-by';
import {QUOTE_DETAILS} from '../helpers';
import {QuoteInfo} from '../read-only/quote-info';

type Props = {
    type: SalesDocumentType;
    users: UsersConnection;
    onSave: (arg1: SalesDocumentUpdateInput) => void;
    onCancel: () => void;
    isActive: boolean;
    id: string;
    preparedBy: string;
    preparedTime: string;
    validUntilDate: string | null | undefined;
    displayNumber: string;
    preparedTimeFormatted: string;
    validUntilDateFormatted: string;
    onToggleActive: (arg1: string) => void;
    forceSave: boolean;
    isImmutable: boolean;
    // Invoice-specific fields that are undefined for Quotes and null when an
    // Invoice does not want to display them
    purchaseOrderNumber?: string | null | undefined;
    jobNumber?: string | null | undefined;
    customerNumber?: string | null | undefined;
    referenceNumber?: string | null | undefined;
};

export function QuotePreparedByWrapper(props: Props) {
    const {id, onSave} = props;

    const [preparedBy, setPreparedBy] = React.useState<string>(props.preparedBy);
    const [preparedTime, setPreparedTime] = React.useState<string>(props.preparedTime);
    const [validUntilDate, setValidUntilDate] = React.useState<string | null | undefined>(
        props.validUntilDate
    );
    const [purchaseOrderNumber, setPurchaseOrderNumber] = React.useState<string | null | undefined>(
        props.purchaseOrderNumber
    );
    const [jobNumber, setJobNumber] = React.useState<string | null | undefined>(props.jobNumber);
    const [customerNumber, setCustomerNumber] = React.useState<string | null | undefined>(
        props.customerNumber
    );
    const [referenceNumber, setReferenceNumber] = React.useState<string | null | undefined>(
        props.referenceNumber
    );

    React.useEffect(() => {
        setPreparedBy(props.preparedBy);
        setPreparedTime(props.preparedTime);
        setValidUntilDate(props.validUntilDate);
        setPurchaseOrderNumber(props.purchaseOrderNumber);
        setJobNumber(props.jobNumber);
        setCustomerNumber(props.customerNumber);
        setReferenceNumber(props.referenceNumber);
    }, [
        props.preparedBy,
        props.preparedTime,
        props.validUntilDate,
        props.purchaseOrderNumber,
        props.jobNumber,
        props.customerNumber,
        props.referenceNumber,
    ]);

    const handleSave = React.useCallback(() => {
        onSave({
            salesDocument: id,
            preparedBy,
            preparedTime,
            validUntilDate,
            purchaseOrderNumber,
            jobNumber,
            customerNumber,
            referenceNumber,
        });
    }, [
        id,
        onSave,
        preparedBy,
        preparedTime,
        validUntilDate,
        purchaseOrderNumber,
        jobNumber,
        customerNumber,
        referenceNumber,
    ]);

    React.useEffect(() => {
        if (props.forceSave) {
            handleSave();
        }
    }, [props.forceSave, handleSave]);

    const handleCancel = () => {
        setPreparedBy(props.preparedBy);
        setPreparedTime(props.preparedTime);
        setValidUntilDate(props.validUntilDate);
        setPurchaseOrderNumber(props.purchaseOrderNumber);
        setJobNumber(props.jobNumber);
        setCustomerNumber(props.customerNumber);
        setReferenceNumber(props.referenceNumber);

        props.onCancel();
    };

    return (
        <SaveableWrapper
            isActive={props.isActive}
            onSave={handleSave}
            onCancel={handleCancel}
            onToggleActive={props.onToggleActive}
            sectionName={QUOTE_DETAILS}
            overflowVisible={true}
            isImmutable={props.isImmutable}
        >
            {props.isActive ? (
                <QuotePreparedBy
                    type={props.type}
                    users={props.users}
                    preparedBy={preparedBy}
                    preparedTime={preparedTime}
                    onPreparedByChange={setPreparedBy}
                    onPreparedTimeChange={setPreparedTime}
                    onValidUntilDateChange={setValidUntilDate}
                    onPurchaseOrderNumberChange={setPurchaseOrderNumber}
                    onJobNumberChange={setJobNumber}
                    onCustomerNumberChange={setCustomerNumber}
                    onReferenceNumberChange={setReferenceNumber}
                    quoteNumber={props.displayNumber}
                    validUntilDate={validUntilDate}
                    purchaseOrderNumber={purchaseOrderNumber}
                    jobNumber={jobNumber}
                    customerNumber={customerNumber}
                    referenceNumber={referenceNumber}
                />
            ) : (
                <QuoteInfo
                    type={props.type}
                    displayNumber={props.displayNumber}
                    preparedBy={props.preparedBy}
                    preparedTimeFormatted={props.preparedTimeFormatted}
                    validUntilDateFormatted={props.validUntilDateFormatted}
                    purchaseOrderNumber={props.purchaseOrderNumber}
                    jobNumber={props.jobNumber}
                    customerNumber={props.customerNumber}
                    referenceNumber={props.referenceNumber}
                />
            )}
        </SaveableWrapper>
    );
}
